import React, { useState } from "react";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
    Dialog, DialogActions, DialogContent, DialogTitle, IconButton, TextField, Button, Box
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { CustomerService } from "../../../service/customers.service";

interface Props {
    open: boolean;
    onClose: () => void;
    callback: () => void;   // called after a successful create so the parent can refresh its list
}

// Admin form to create a new retail user. Mirrors the backend 'addUserManually' contract
// (username / mobileno / mailid / pin); the backend enforces a 10-digit mobile + valid email
// and rejects duplicates, but we validate up front for a cleaner UX.
const AddRetailUserDialog = ({ open, onClose, callback }: Props) => {
    const customerService = new CustomerService();

    const [username, setUsername] = useState("");
    const [mobileno, setMobileno] = useState("");
    const [mailid, setMailid] = useState("");
    const [pin, setPin] = useState("");
    const [saving, setSaving] = useState(false);

    const reset = () => { setUsername(""); setMobileno(""); setMailid(""); setPin(""); };
    const handleClose = () => { if (!saving) { reset(); onClose(); } };

    const submit = () => {
        const mobile = mobileno.trim();
        if (username.trim() === "") {
            toast.error("Name is required", { position: toast.POSITION.BOTTOM_CENTER, autoClose: 2000, hideProgressBar: true });
            return;
        }
        if (!/^[0-9]{10}$/.test(mobile)) {
            toast.error("Mobile number must be 10 digits", { position: toast.POSITION.BOTTOM_CENTER, autoClose: 2000, hideProgressBar: true });
            return;
        }
        setSaving(true);
        customerService.addRetailUser(username.trim(), mobile, mailid.trim(), pin.trim()).then((res: any) => {
            setSaving(false);
            if (res != null && res.status == 100) {
                toast.success("Retail user added successfully !!!", { position: toast.POSITION.BOTTOM_CENTER, autoClose: 2000, hideProgressBar: true });
                reset();
                callback();
                onClose();
            } else {
                toast.error((res && res.msg) ? res.msg : "Action Failed !!!", { position: toast.POSITION.BOTTOM_CENTER, autoClose: 2500, hideProgressBar: true });
            }
        }).catch((err: any) => {
            setSaving(false);
            console.log('addRetailUser err ', err);
            toast.error("Network error while adding user", { position: toast.POSITION.BOTTOM_CENTER, autoClose: 2500, hideProgressBar: true });
        });
    };

    return (
        <Dialog fullWidth maxWidth="sm" open={open} onClose={handleClose} aria-labelledby="add-retail-user-title">
            <DialogTitle sx={{ m: 0, p: 2 }} id="add-retail-user-title">
                Add Retail User
                <IconButton aria-label="close" onClick={handleClose}
                    sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <TextField label="Name" value={username} onChange={(e) => setUsername(e.target.value)} fullWidth required />
                    <TextField label="Mobile Number" value={mobileno}
                        onChange={(e) => setMobileno(e.target.value.replace(/[^0-9]/g, '').slice(0, 10))}
                        fullWidth required inputProps={{ inputMode: 'numeric', maxLength: 10 }} helperText="10 digits" />
                    <TextField label="Email" type="email" value={mailid} onChange={(e) => setMailid(e.target.value)} fullWidth />
                    <TextField label="PIN" value={pin}
                        onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, '').slice(0, 4))}
                        fullWidth inputProps={{ inputMode: 'numeric', maxLength: 4 }} helperText="Up to 4 digits (optional)" />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} disabled={saving}>Cancel</Button>
                <Button onClick={submit} variant="contained" disabled={saving}>{saving ? "Saving..." : "Add User"}</Button>
            </DialogActions>
        </Dialog>
    );
};

export default AddRetailUserDialog;
